import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Participant, RemoteParticipant } from 'livekit-client'

import { Chevron } from '@/src/assets/icons'
import { ParticipantListItem } from '@/src/components/ParticipantListItem'
import { useMaybeRoomContext, useMaybeLayoutContext } from '@/src/context'
import { useGroupRemainingTime } from '@/src/hooks/useGroupRemainingTime'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'
import { selectRoomState } from '@/src/store/slices/room'
import { selectAccessToken } from '@/src/store/slices/token'

export interface GroupListItemProps {
  groupId: string 
  groupName: string 
  participants: (Participant | RemoteParticipant)[] 
  isCurrentUserGroup?: boolean 
  groupOptions?: { id: string | null; name: string }[]
  isExpanded?: boolean
  onToggle?: (groupId: string) => void
}

export function GroupListItem({
  groupId,
  groupName,
  participants,
  isCurrentUserGroup = false,
  groupOptions = [],
  isExpanded: externalIsExpanded,
  onToggle,
}: GroupListItemProps) {
  const { t } = useTranslation()
  const [internalIsExpanded, setInternalIsExpanded] = useState(false)
  
  // Используем внешнее состояние если передано, иначе внутреннее
  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded
  
  const handleToggle = useCallback(() => {
    if (onToggle) {
      onToggle(groupId)
    } else {
      setInternalIsExpanded(!internalIsExpanded)
    }
  }, [onToggle, groupId, internalIsExpanded])
  const [isJoining, setIsJoining] = useState(false)
  const { text_primary_color, primary_color } = useAppSelector(selectBranding)
  const roomContext = useMaybeRoomContext()
  const layoutContext = useMaybeLayoutContext()
  const isAdmin = useAppSelector(selectIsAdmin)
  const { livekit_url } = useAppSelector(selectRoomState)
  const accessToken = useAppSelector(selectAccessToken)

  // Проверяем, является ли локальный участник куратором
  const localIsCurator = roomContext?.localParticipant?.attributes?.isCurator === 'true'

  // Админы и кураторы могут входить в группы
  const canJoinGroup = isAdmin || localIsCurator

  // Обработчик для присоединения к группе
  const handleJoinGroup = useCallback(async () => {
    if (!roomContext?.name || !livekit_url) return

    const identity = roomContext.localParticipant?.identity
    if (!identity) return

    setIsJoining(true)

    // Сбрасываем pin при переходе в группу
    layoutContext?.pin.dispatch?.({ msg: 'clear_pin' })

    try {
      const response = await fetch(
        `${window.origin}/api/move_participant?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roomName: roomContext.name,
            participantIdentity: identity,
            groupId: groupId,
          }),
        },
      )

      if (!response.ok) {
        console.error('Failed to join group:', await response.text())
      }
    } catch (error) {
      console.error('Error joining group:', error)
    } finally {
      setIsJoining(false)
    }
  }, [roomContext, livekit_url, accessToken, groupId, layoutContext?.pin])

  // Обработчик для выхода из группы в основной зал
  const handleLeaveGroup = useCallback(async () => {
    if (!roomContext?.name || !livekit_url) return

    const identity = roomContext.localParticipant?.identity
    if (!identity) return

    setIsJoining(true)

    // Сбрасываем pin при выходе из группы
    layoutContext?.pin.dispatch?.({ msg: 'clear_pin' })

    try {
      const response = await fetch(
        `${window.origin}/api/move_participant?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            authorization: accessToken || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roomName: roomContext.name,
            participantIdentity: identity,
            groupId: null, // null означает основной зал
          }),
        },
      )

      if (!response.ok) {
        console.error('Failed to leave group:', await response.text())
      }
    } catch (error) {
      console.error('Error leaving group:', error)
    } finally {
      setIsJoining(false)
    }
  }, [roomContext, livekit_url, accessToken, layoutContext?.pin])

  const filteredParticipants = useMemo(() => {
    if (!roomContext) return participants

    // Фильтруем участников по поиску, если есть
    return participants.filter((participant) => {
      // Исключаем скрытых участников 
      if (participant.permissions?.hidden) return false
      // Исключаем агентов и рекордеров 
      if (participant.permissions?.agent || participant.permissions?.recorder) return false
      return true
    })
  }, [participants, roomContext])

  const participantCount = filteredParticipants.length

  const { hasActiveTimer: hasTimer, remainingTime } = useGroupRemainingTime(
    roomContext?.metadata,
    isExpanded,
  )

  return (
    <div className={`lk-group-item ${isCurrentUserGroup ? 'lk-group-item-current' : ''}`}>
      <div
        className="lk-group-item-header"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`${groupName}, ${participantCount} участников`}
      >
        <div className="lk-group-item-header-content">
          <span className="lk-group-item-title">
            {groupName}
            {hasTimer && !isExpanded && (
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '12px',
                  opacity: 0.7,
                }}
                title="У группы установлен таймер"
              >
                ⏱️
              </span>
            )}
          </span>
          <span className="lk-group-item-count">{participantCount}</span>
        </div>
        <div 
          className="lk-group-item-actions"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginRight: '-4px',
          }}
        >
          {/* Кнопка "Войти" / "Вы тут" в шапке группы */}
          {canJoinGroup && (
            isCurrentUserGroup ? (
              <span
                className="lk-group-you-here-badge"
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: primary_color || '#6366f1',
                  background: primary_color 
                    ? `color-mix(in srgb, ${primary_color} 15%, transparent)`
                    : 'rgba(99, 102, 241, 0.15)',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('youAreHere')}
              </span>
            ) : (
              <button
                className="lk-group-join-button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleJoinGroup()
                }}
                disabled={isJoining}
                style={{
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#fff',
                  background: primary_color || '#6366f1',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isJoining ? 'wait' : 'pointer',
                  opacity: isJoining ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!isJoining) {
                    e.currentTarget.style.opacity = '0.85'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = isJoining ? '0.7' : '1'
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                {isJoining ? t('joining') : t('joinGroup')}
              </button>
            )
          )}
          <Chevron
            className={`lk-group-item-chevron ${isExpanded ? 'lk-group-item-chevron-expanded' : ''}`}
            style={{ color: `${text_primary_color}` }}
          />
        </div>
      </div>
      {isExpanded && (
        <div className="lk-group-item-participants">
          {remainingTime && (
            <div
              className="lk-group-timer"
              style={{
                padding: '8px 12px',
                margin: '8px 12px',
                borderRadius: '4px',
                backgroundColor: primary_color 
                  ? `color-mix(in srgb, ${primary_color} 15%, transparent)` 
                  : 'rgba(255, 255, 255, 0.1)',
                color: text_primary_color || '#ffffff',
                fontSize: '14px',
                fontWeight: 500,
                textAlign: 'center',
                border: `1px solid ${primary_color || 'transparent'}`,
              }}
            >
              {t('groupWillFinishIn', { time: remainingTime })}
            </div>
          )}
          {filteredParticipants.length > 0 ? (
            filteredParticipants.map((participant) => (
              <ParticipantListItem
                key={participant.identity}
                participant={participant}
                groupOptions={groupOptions}
              />
            ))
          ) : (
            <div className="lk-group-item-empty">{t('emptyGroupMessage')}</div>
          )}
        </div>
      )}
    </div>
  )
}

